// Admin app router. Match Core's adminMenu route structure cho `chat` group:
// /inbox + /inbox/canned-responses + /inbox/agents + /inbox/knowledge-base
// + /inbox/ai-training + /inbox/ai-logs + /inbox/channels + /inbox/config
// SaaS-specific (API Keys, Webhooks) đặt vào group `system` theo Core.

import { Suspense } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import RequireAuth from './components/RequireAuth';
import lazyWithReload from '@cnv/ui/utils/lazyWithReload';
import { baseURL as PUBLIC_API_BASE } from './lib/inboxClient';
// Vỏ bọc cho dãy tiện ích nền cuối tệp: <Suspense> chỉ đỡ lúc TẢI chunk, không đỡ lúc
// widget NÉM — mà ở đây cái tóm được là Sentry.ErrorBoundary bọc CẢ <App/> (main.jsx),
// nên một widget nền hỏng là thay sạch giao diện bằng trang "Đã có lỗi xảy ra".
import BackgroundWidget from '@cnv/ui/common/BackgroundWidget';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
// Quên mật khẩu: KHÔNG lazy — người dùng tới đây từ link trong email, chunk lỗi/stale sau
// deploy là trang trắng ngay ở bước cứu tài khoản. Cùng lý do LoginPage cũng import tĩnh.
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
const SettingUpPage = lazyWithReload(() => import('./pages/SettingUpPage'));
const InboxPage = lazyWithReload(() => import('./pages/InboxPage'));
const InvitationsPage = lazyWithReload(() => import('./pages/InvitationsPage'));
import AcceptInvitePage from './pages/AcceptInvitePage';
const WorkspaceSelectorPage = lazyWithReload(() => import('./pages/WorkspaceSelectorPage'));
const OAuthConsentPage = lazyWithReload(() => import('./pages/OAuthConsentPage'));
// Public share (ADR-032) — KHÁCH HÀNG mở, không đăng nhập. Clone 1:1 từ Core
// attendance-app/admin/src/pages/public/ (chỉ đổi API base + import productUnit + branding footer).
const PublicQuotationPage = lazyWithReload(() => import('./pages/public/PublicQuotationPage'));
const PublicInvoicePage = lazyWithReload(() => import('./pages/public/PublicInvoicePage'));
// Lịch xếp việc CHIA SẺ (port máy chủ vòng 3→7 từ Connect 22/09/2026) — trang kernel, gọi API bằng
// địa chỉ tuyệt đối host đưa vào (`baseURL` = VITE_API_BASE_URL, prod `https://api.cnvwork.com/api/v1`).
const PublicSchedulePage = lazyWithReload(() => import('@cnv/ui/pages/projects/PublicSchedulePage'));
// Phiếu nghỉ việc điền SAU KHI KHOÁ tài khoản — link bảo mật /f/:token (quy trình nghỉ việc, port Core 01/10/2026).
// Trang gọi `fetch` trần nên PHẢI truyền apiBase tuyệt đối (mặc định của trang là '/api' — hub không proxy).
const PublicProcessFormPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/PublicProcessFormPage'));
// Biểu mẫu lead HOST SẴN (22/10/2026) — đường lùi của mã nhúng website, nạp chính mã nhúng ở chế độ trang.
const PublicLeadFormPage = lazyWithReload(() => import('./pages/public/PublicLeadFormPage'));
// `/f/:token` dùng chung cho HAI loại link công khai, tách theo DẠNG mã: khoá biểu mẫu lead `lf_` + 24 hex (27 ký tự,
// kernel leadFormEmbed.PUBLIC_KEY_RE) không bao giờ trùng token phiếu nghỉ việc (base64url 43 ký tự). Giữ link ngắn
// hub.cnvwork.com/f/<mã> cho bio / mã QR mà không đụng link phiếu nghỉ việc đã gửi đi.
const LEAD_FORM_KEY_RE = /^lf_[0-9a-f]{24}$/;
function PublicFormRoute() {
  const { token } = useParams();
  return LEAD_FORM_KEY_RE.test(token || '')
    ? <PublicLeadFormPage />
    : <PublicProcessFormPage apiBase={PUBLIC_API_BASE} />;
}
// /inbox/channels — port 1:1 từ Core (cnv-ui), thay cho SaaS's ChannelsPage M1 tự bịa
const ChannelsPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatChannelsPage'));
// Một bản cho Core + Work (kernel @cnv/ui, 14/10/2026) — trước đó mỗi admin một bản chép tay.
const ZaloCampaignsPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ZaloCampaignsPage'));
const ChatStaffPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatStaffPage'));
const ApiKeysPage = lazyWithReload(() => import('./pages/ApiKeysPage'));
const McpKeysPage = lazyWithReload(() => import('./pages/McpKeysPage'));
// Nhật ký kiểm toán — SaaS-only (form bảo mật khách BE: IRM-05 · IAM-05 · CLD-04). docs/PORT_GAPS.md 2026-10-20.
const AuditLogPage = lazyWithReload(() => import('@cnv/ui/pages/settings/AuditLogPage'));
const WebhooksPage = lazyWithReload(() => import('./pages/WebhooksPage'));
const MarketplacePage = lazyWithReload(() => import('@cnv/ui/pages/marketplace/MarketplacePage'));
// Bản quyền tính năng — admin chia quota đã mua (chỗ Hộp thư, máy nhánh…) cho từng nhân viên.
// SaaS-only: Core self-hosted không bán theo chỗ ngồi. Xem docs/PORT_GAPS.md.
const FeatureLicensesPage = lazyWithReload(() => import('@cnv/ui/pages/settings/FeatureLicensesPage'));
// SaaS-only — công tắc «Tắt toàn bộ AI» cấp workspace (form bảo mật khách BE, AI-01). docs/PORT_GAPS.md 2026-10-20.
const AiSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/AiSettingsPage'));
// Trang kích hoạt gói — khách bấm mua trong Hộp thư / Kho ứng dụng thì tới đây chuyển khoản.
const SelfOrderPage = lazyWithReload(() => import('@cnv/ui/pages/marketplace/SelfOrderPage'));

// Team Chat (Slack/Lark-style) — port từ Core attendance-app.
// Cross-module dep FulfillmentOrderDrawerContext đã stub (navigate full page khi
// chưa port fulfillment) — xem cnv-ui/src/components/fulfillment/FulfillmentOrderDrawerContext.jsx
const TeamChatPage = lazyWithReload(() => import('@cnv/ui/pages/team-chat/TeamChatPage'));

// Push notifications — re-export wrappers point tới @cnv/ui (share kernel).
// Match Core's main.jsx pattern. Trong CNV Work desktop (Electron), 3 component
// này detect window.cnvDesktop và route qua native macOS/Win/Linux noti thay
// vì Web Push (Push API unavailable trong Electron).
const ChatPushNotifications = lazyWithReload(() => import('./components/chat/ChatPushNotifications'));
const TeamChatPushNotifications = lazyWithReload(() => import('./components/chat/TeamChatPushNotifications'));
const WebPushRegister = lazyWithReload(() => import('./components/chat/WebPushRegister'));
const CommandPalette = lazyWithReload(() => import('@cnv/ui/common/CommandPalette'));
const WebVersionWatcher = lazyWithReload(() => import('@cnv/ui/common/WebVersionWatcher'));
const QuickScreenshotShortcut = lazyWithReload(() => import('@cnv/ui/feedback/QuickScreenshotShortcut'));
const FloatingCallWidget = lazyWithReload(() => import('./components/voip/FloatingCallWidget'));
const HelpWidgetLoader = lazyWithReload(() => import('@cnv/ui/support-help/HelpWidgetLoader'));

// Inbox sub-pages — clone từ Core (cnv-ui)
const ChatCannedResponsesPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatCannedResponsesPage'));
const ChatAgentsPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatAgentsPage'));
const ChatAgentDetailPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatAgentDetailPage'));
const KnowledgeBasePage = lazyWithReload(() => import('@cnv/ui/pages/inbox/KnowledgeBasePage'));
const ChatAITrainingPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatAITrainingPage'));
const ChatAILogsPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatAILogsPage'));
const ChatConfigPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatConfigPage'));
// Phase D2: Inbox Reports (metrics + QC + ROI + performance)
const ChatMetricsPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatMetricsPage'));
const ChatQcReviewsPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatQcReviewsPage'));
const ChatReportsPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatReportsPage'));
const ChatPerformanceReportPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatPerformanceReportPage'));
const ChatRoiReportPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatRoiReportPage'));
const ChatCustomerEngagementReportPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatCustomerEngagementReportPage'));

// CRM Sales pages (M4 ADR-022 — internal-only) — clone từ Core (cnv-ui)
const CrmDashboardPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmDashboardPage'));
const CrmLeadsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmLeadsPage'));
const CrmLeadDetailPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmLeadDetailPage'));
const CrmContactsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmContactsPage'));
const CrmContactDetailPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmContactDetailPage'));
const CrmCompaniesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmCompaniesPage'));
const CrmCompanyDetailPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmCompanyDetailPage'));
const CrmDealsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmDealsPage'));
const CrmDealDetailPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmDealDetailPage'));
const CrmActivitiesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmActivitiesPage'));
const RoutinesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/RoutinesPage'));
const CrmSourcesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmSourcesPage'));

// CRM Finance + Promotion pages (M5 ADR-022 — internal-only) — clone từ Core (cnv-ui)
const CrmQuotationsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmQuotationsPage'));
const CrmQuotationDetailPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmQuotationDetailPage'));
const CrmInvoicesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmInvoicesPage'));
const CrmInvoiceDetailPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmInvoiceDetailPage'));
const CrmPaymentsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmPaymentsPage'));
const CrmTargetsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmTargetsPage'));
const CrmPaymentMethodsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmPaymentMethodsPage'));
const CrmSubscriptionsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmSubscriptionsPage'));
const CrmPromotionsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmPromotionsPage'));
const CrmPromotionDetailPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmPromotionDetailPage'));
// CrmCouponsPage không import — Core route redirect /crm/coupons → /crm/promotions

// Phase B: Products + Categories
const CrmProductsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmProductsPage'));
const CrmProductCategoriesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmProductCategoriesPage'));
const CrmLeadObjectivesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmLeadObjectivesPage'));
// Phase C1: CreditNotes
const CrmCreditNotesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmCreditNotesPage'));
// Phase C2: DealRoles + Industries
const CrmDealRolesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmDealRolesPage'));
const CrmIndustriesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmIndustriesPage'));
// Phase C3: DocTemplates + Documents + DataTransfer + DuplicateCheck + RoundRobin
const CrmDocTemplatesPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmDocTemplatesPage'));
const CrmDocumentsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmDocumentsPage'));
const CrmDataTransferPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmDataTransferPage'));
const CrmDataTransferSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmDataTransferSettingsPage'));
const CrmDuplicateSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmDuplicateSettingsPage'));
const CrmLeadScoringPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmLeadScoringPage'));
const LeadClaimRequestsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/LeadClaimRequestsPage'));
// Port từ Core 2026-05-12 (sync gap): Commissions + Revenue + ContractEnd report
const CrmCommissionsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmCommissionsPage'));
const SalesPerformancePage = lazyWithReload(() => import('@cnv/ui/pages/reports/SalesPerformancePage')); // migrate Core→shared
// «Forecast» + «Team báo cáo» — 2 thẻ cuối trong kho báo cáo còn ra màn "đang được phát triển"
// (catalog dùng chung với Core có thẻ, SaaS chưa port page + BE). Port 17/08.
// SalesTeamPage nhận `mattermostEnabled` mặc định false: SaaS đã gỡ tích hợp Mattermost nên 2 ô
// «Channel» + «Template» của Core không hiện. Xem docs/PORT_GAPS.md.
const SalesForecastPage = lazyWithReload(() => import('@cnv/ui/pages/sales/ForecastPage'));
const SalesTeamPage = lazyWithReload(() => import('@cnv/ui/pages/sales/SalesTeamPage'));
const HrAttendanceReportPage = lazyWithReload(() => import('@cnv/ui/pages/reports/ReportsPage'));
const HrAssetReportPage = lazyWithReload(() => import('@cnv/ui/pages/reports/AssetReportPage'));
const HrAssetAssignmentReportPage = lazyWithReload(() => import('@cnv/ui/pages/reports/AssetAssignmentReportPage'));
const CrmRevenuePage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmRevenuePage'));
// Phase C3: Quotation Approval Settings (đã clone từ trước, em add wire)
const CrmQuotationApprovalSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmQuotationApprovalSettingsPage'));
const CrmInvoiceBillingSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmInvoiceBillingSettingsPage'));
// «Trường dữ liệu» (02/10/2026) thay trang «Trường bắt buộc Lead»: trường hệ thống + trường tùy chỉnh 4 đối tượng CRM.
const CrmFieldSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmFieldSettingsPage'));

// Reports Hub — top-level theo Core (adminMenu line 193-200), KHÔNG nested trong CRM.
const ReportsHub = lazyWithReload(() => import('@cnv/ui/pages/reports/ReportsHub'));
const ReportRoute = lazyWithReload(() => import('@cnv/ui/pages/reports/ReportRoute'));

// Approvals (M6) — port từ Core
import ApprovalShell from '@cnv/ui/approvals/ApprovalShell';
const ApprovalInboxPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/ApprovalInboxPage'));
const ApprovalTemplatesPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/ApprovalTemplatesPage'));
const ApprovalDetailPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/ApprovalDetailPage'));
const TemplateBuilderPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/TemplateBuilderPage'));
const ApprovalReportPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/ApprovalReportPage'));
const MyPendingPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/MyPendingPage'));
const MyRequestsPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/MyRequestsPage'));
const SharedListsPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/SharedListsPage'));
const ApprovalsPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/ApprovalsPage'));

// Dashboard (M6) — port từ Core
const DashboardPage = lazyWithReload(() => import('@cnv/ui/pages/dashboard/DashboardPage'));

// HR (M6.2b) — Employees + Teams + OrgChart (port từ Core)
const EmployeesPage = lazyWithReload(() => import('@cnv/ui/pages/employees/EmployeesPage'));
const EmployeeDetailPage = lazyWithReload(() => import('@cnv/ui/pages/employees/EmployeeDetailPage'));
const EmployeeEventsPage = lazyWithReload(() => import('@cnv/ui/pages/employees/EventsPage'));
const OrgChartPage = lazyWithReload(() => import('@cnv/ui/pages/employees/OrgChartPage'));
const ContractsPage = lazyWithReload(() => import('@cnv/ui/pages/employees/ContractsPage'));
const ContractTypesPage = lazyWithReload(() => import('@cnv/ui/pages/employees/ContractTypesPage'));
const JobTitlesPage = lazyWithReload(() => import('@cnv/ui/pages/employees/JobTitlesPage'));
const TeamsPage = lazyWithReload(() => import('@cnv/ui/pages/teams/TeamsPage'));

// HR Leaves + Holidays (M6.3) — đơn nghỉ phép + số dư phép + lịch nghỉ lễ
const LeavesPage = lazyWithReload(() => import('@cnv/ui/pages/leaves/LeavesPage'));
const LeaveBalancePage = lazyWithReload(() => import('@cnv/ui/pages/leaves/LeaveBalancePage'));
const HolidaysPage = lazyWithReload(() => import('@cnv/ui/pages/holidays/HolidaysPage'));

// HR Shifts (M6.4) — ca làm việc (prerequisite cho Attendance)
const ShiftsPage = lazyWithReload(() => import('@cnv/ui/pages/shifts/ShiftsPage'));
// Tăng ca — hai màn dùng chung với Core (@cnv/ui). Cửa API: /api/v1/overtime/*.
const OvertimePage = lazyWithReload(() => import('@cnv/ui/pages/attendance/OvertimePage'));
const OvertimePolicyPage = lazyWithReload(() => import('@cnv/ui/pages/attendance/OvertimePolicyPage'));
// Lịch tuần (quản lý) + Lịch làm việc và ca trống (nhân viên) — dùng chung với Core. Cửa API:
// /api/v1/attendance-roster/* (19/09/2026), mở hai cửa tăng ca roster_planned + open_shift_claim.
const RosterPage = lazyWithReload(() => import('@cnv/ui/pages/attendance/RosterPage'));
const MyRosterPage = lazyWithReload(() => import('@cnv/ui/pages/attendance/MyRosterPage'));
const TimesheetCreditRulesPage = lazyWithReload(() => import('@cnv/ui/pages/shifts/TimesheetCreditRulesPage'));

// HR Locations (M6.5) — geofencing check-in
const LocationsPage = lazyWithReload(() => import('@cnv/ui/pages/locations/LocationsPage'));

// Automation (M6.11) — flow builder + runs
const AutomationListPage = lazyWithReload(() => import('@cnv/ui/pages/automation/AutomationListPage'));
const AutomationBuilderPage = lazyWithReload(() => import('@cnv/ui/pages/automation/AutomationBuilderPage'));

// Settings (M6.12) — 14 pages: tenant config + RBAC + templates + integrations
const SettingsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/SettingsPage'));
const StorageUsagePage = lazyWithReload(() => import('@cnv/ui/pages/settings/StorageUsagePage'));
const NotificationLogsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/NotificationLogsPage'));
const KanbanThemePage = lazyWithReload(() => import('@cnv/ui/pages/settings/KanbanThemePage'));
const RolesPage = lazyWithReload(() => import('@cnv/ui/pages/settings/RolesPage'));
const PermissionsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/PermissionsPage'));
const LeadDistributionPage = lazyWithReload(() => import('@cnv/ui/pages/settings/LeadDistributionPage'));
const IntegrationChatGPTPage = lazyWithReload(() => import('@cnv/ui/pages/settings/IntegrationChatGPTPage'));
const IntegrationClaudePage = lazyWithReload(() => import('@cnv/ui/pages/settings/IntegrationClaudePage'));
const MicrosoftIntegrationPage = lazyWithReload(() => import('@cnv/ui/pages/settings/MicrosoftIntegrationPage'));
const GoogleSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/GoogleSettingsPage'));
// Mattermost integration bỏ khỏi SaaS (2026-05-11) — Core attendance-app vẫn dùng.
const EmailTemplatesPage = lazyWithReload(() => import('@cnv/ui/pages/settings/email-templates/EmailTemplatesPage'));
// Fireflies integration bỏ khỏi SaaS (2026-07-11) — Core attendance-app vẫn dùng.
// CNV Chat integration bỏ khỏi SaaS (2026-07-25) — SaaS tự đăng ký chat-service. Core vẫn dùng.
const BotsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/BotsPage'));
const BotDetailPage = lazyWithReload(() => import('@cnv/ui/pages/settings/BotDetailPage'));
const EtelecomSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/EtelecomSettingsPage'));

// Lương ẨN 07/10/2026 (sếp Phú: «hệ này làm chưa xong») — xem chú thích nhóm «Lương» ở AppLayout.jsx.
// Payroll (M6.10) — periods, structures, levels, insurance, tax
// const PayrollPeriodsPage = lazyWithReload(() => import('@cnv/ui/pages/payroll/PayrollPeriodsPage'));
// const PayrollPeriodDetailPage = lazyWithReload(() => import('@cnv/ui/pages/payroll/PayrollPeriodDetailPage'));
// const PayrollLevelsPage = lazyWithReload(() => import('@cnv/ui/pages/payroll/PayrollLevelsPage'));
// const PayrollStructuresPage = lazyWithReload(() => import('@cnv/ui/pages/payroll/PayrollStructuresPage'));
// const PayrollInsurancePage = lazyWithReload(() => import('@cnv/ui/pages/payroll/PayrollInsurancePage'));
// const PayrollTaxPage = lazyWithReload(() => import('@cnv/ui/pages/payroll/PayrollTaxPage'));

// Attendance (M6.9) — chấm công + corrections + penalty rules + timesheet
const AttendancePage = lazyWithReload(() => import('@cnv/ui/pages/attendance/AttendancePage'));
const CorrectionReviewPage = lazyWithReload(() => import('@cnv/ui/pages/attendance/CorrectionReviewPage'));
const MonthlyTimesheetPage = lazyWithReload(() => import('@cnv/ui/pages/timesheet/MonthlyTimesheetPage'));
const WeeklyTimesheetPage = lazyWithReload(() => import('@cnv/ui/pages/timesheet/WeeklyTimesheetPage'));
const LatePenaltyPage = lazyWithReload(() => import('@cnv/ui/pages/penalties/LatePenaltyPage'));

// Khách hàng (Customers 360°) — SaaS-only. URL /crm/customers khớp Core (parity 2026-06-05).
const CrmCustomersPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmCustomersPage'));
const CrmCustomerDetailPage = lazyWithReload(() => import('@cnv/ui/pages/crm/CrmCustomerDetailPage'));

// Tiện ích — Nhà cung cấp + Kho in ấn
const SupplierListPage = lazyWithReload(() => import('@cnv/ui/pages/suppliers/SupplierListPage'));
// Export Center (Tiện ích → Xuất file) — docs/EXPORT_CENTER.md.
const ExportsPage = lazyWithReload(() => import('@cnv/ui/pages/utilities/ExportsPage'));
const SupplierDetailPage = lazyWithReload(() => import('@cnv/ui/pages/suppliers/SupplierDetailPage'));
const PrintLibraryPage = lazyWithReload(() => import('@cnv/ui/pages/printing/PrintLibraryPage'));
const NotificationTemplateListPage = lazyWithReload(() => import('@cnv/ui/pages/utilities/NotificationTemplateListPage'));
const NotificationTemplateDetailPage = lazyWithReload(() => import('@cnv/ui/pages/utilities/NotificationTemplateDetailPage'));
const CompanyRoomPage = lazyWithReload(() => import('@cnv/ui/pages/utilities/CompanyRoomPage'));
const CabinTranslatePage = lazyWithReload(() => import('@cnv/ui/pages/utilities/CabinTranslatePage'));
const CabinSessionPage = lazyWithReload(() => import('@cnv/ui/pages/utilities/CabinSessionPage'));
const EmailSignaturePage = lazyWithReload(() => import('@cnv/ui/pages/utilities/EmailSignaturePage'));
// Tin mới sản phẩm (chuông «Tin mới») — trang đọc dùng chung với Core; bài soạn ở Core.
const ProductNewsPage = lazyWithReload(() => import('@cnv/ui/product-news/ProductNewsPage'));
const ProductNewsDetailPage = lazyWithReload(() => import('@cnv/ui/product-news/ProductNewsDetailPage'));
const PrintItemDetailPage = lazyWithReload(() => import('@cnv/ui/pages/printing/PrintItemDetailPage'));

// Marketing (M6.8) — campaigns + lead-forms + events + memberships
const MarketingDashboardPage = lazyWithReload(() => import('@cnv/ui/pages/marketing/MarketingDashboardPage'));
const MarketingCampaignsPage = lazyWithReload(() => import('@cnv/ui/pages/marketing/MarketingCampaignsPage'));
const MarketingCampaignDetailPage = lazyWithReload(() => import('@cnv/ui/pages/marketing/MarketingCampaignDetailPage'));
const MarketingAdAccountsPage = lazyWithReload(() => import('@cnv/ui/pages/marketing/MarketingAdAccountsPage'));
const MarketingLeadFormsPage = lazyWithReload(() => import('@cnv/ui/pages/marketing/MarketingLeadFormsPage'));
const MarketingEventsPage = lazyWithReload(() => import('@cnv/ui/pages/marketing/MarketingEventsPage'));
const MarketingEventDetailPage = lazyWithReload(() => import('@cnv/ui/pages/marketing/MarketingEventDetailPage'));
const MarketingMembershipsPage = lazyWithReload(() => import('@cnv/ui/pages/marketing/MarketingMembershipsPage'));
const MarketingMembershipDetailPage = lazyWithReload(() => import('@cnv/ui/pages/marketing/MarketingMembershipDetailPage'));
const MarketingReportsPage = lazyWithReload(() => import('@cnv/ui/pages/marketing/MarketingReportsPage'));

// User profile + me settings — Core port (logout button trong AccountSection của /me)
const MeSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/me/MeSettingsPage'));
const ProfilePage = lazyWithReload(() => import('@cnv/ui/pages/profile/ProfilePage'));
// const MyPayrollPage = lazyWithReload(() => import('@cnv/ui/pages/profile/MyPayrollPage'));

// Assets (M6.6) — quản lý tài sản công ty
const AssetListPage = lazyWithReload(() => import('@cnv/ui/pages/assets/AssetListPage'));
const AssetDetailPage = lazyWithReload(() => import('@cnv/ui/pages/assets/AssetDetailPage'));
const AssetAssignmentPage = lazyWithReload(() => import('@cnv/ui/pages/assets/AssignmentPage'));
const AssetAuditPage = lazyWithReload(() => import('@cnv/ui/pages/assets/AuditPage'));
const DigitalAssetsPage = lazyWithReload(() => import('@cnv/ui/pages/digital-assets/DigitalAssetsPage'));

// Recruitment (M6.7) — JD + Sources + Pipelines + Proposals + Campaigns + Candidates + Onboarding
const RecruitmentDashboard = lazyWithReload(() => import('@cnv/ui/pages/recruitment/RecruitmentDashboard'));
const ProposalsPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/ProposalsPage'));
const CampaignsPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/CampaignsPage'));
const CandidatesPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/CandidatesPage'));
const KanbanPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/KanbanPage'));
const JDLibraryPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/JDLibraryPage'));
const OnboardingPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/OnboardingPage'));
const OffboardingPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/OffboardingPage'));
const SourcesPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/SourcesPage'));
const RecruitmentSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/RecruitmentSettingsPage'));
const PipelineLibraryPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/PipelineLibraryPage'));
const PipelineBuilderPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/PipelineBuilderPage'));
const PipelineSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/PipelineSettingsPage'));

// Tasks (M6) — port từ Core
// Cụm DỰ ÁN — port 1:1 từ CNV Connect 28/08/2026. Năm màn = năm CÁCH NHÌN cùng một tập dữ
// liệu (task_projects), chọn bằng dãy icon trên trang chứ không phải nav phụ.
const ProjectsOverviewPage = lazyWithReload(() => import('./pages/ProjectsOverviewPage'));
const ProjectsBoardPage = lazyWithReload(() => import('./pages/ProjectsBoardPage'));
const ProjectsMapPage = lazyWithReload(() => import('./pages/ProjectsMapPage'));
// «Bản đồ check-in» có URL RIÊNG trở lại từ 04/09/2026 (sếp Phú, quyết ở Connect — kernel
// dùng chung nên ba hệ đi cùng nhịp). ⚠️ BẮT BUỘC đổi cùng lượt với kernel: bản kernel mới
// chuyển hướng `?layer=checkin` → `/projects/checkin-map`, mà route cũ ở đây lại chuyển ngược
// về `?layer=checkin` ⇒ vòng lặp chuyển hướng vô tận, trang trắng.
const ProjectsCheckinMapPage = lazyWithReload(() => import('./pages/ProjectsCheckinMapPage'));
const ProjectsSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/projects/ProjectsSettingsPage'));
// Circle — nhịp làm việc kiểu sprint. Đặt TRƯỚC route `/tasks` bên dưới để đoạn `circles`
// không bị màn Công việc nuốt (TasksPage đọc `/tasks/*` cho dự án + tasklist).
const CirclesPage = lazyWithReload(() => import('@cnv/ui/pages/tasks/CirclesPage'));
const ProjectTemplatesPage = lazyWithReload(() => import('@cnv/ui/pages/projects/ProjectTemplatesPage'));
const ProjectFormTemplatesPage = lazyWithReload(() => import('@cnv/ui/pages/projects/ProjectFormTemplatesPage'));
const ProjectFormTemplateEditPage = lazyWithReload(() => import('@cnv/ui/pages/projects/ProjectFormTemplateEditPage'));
const ProjectProfilePage = lazyWithReload(() => import('./pages/ProjectProfilePage'));
// Kernel hoá 03/09/2026 — bản host (934 dòng) đã trôi 129 dòng so với Connect sau một tuần;
// nay ba hệ cùng một tệp trong @cnv/ui. Xem đầu tệp kernel.
const WorkSchedulePage = lazyWithReload(() => import('@cnv/ui/pages/projects/WorkSchedulePage'));
const TasksPage = lazyWithReload(() => import('@cnv/ui/pages/tasks/TasksPage'));
// TaskBoard, TaskWorkloadView, TemplatesPage — KHÔNG import standalone, render bên trong TasksPage qua ?view= query param (match Core)
const TaskDetailPage = lazyWithReload(() => import('@cnv/ui/pages/tasks/TaskDetailPage'));
const TaskStagesSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/tasks/TaskStagesSettingsPage'));

// Support (M6) — port từ Core
const TicketsPage = lazyWithReload(() => import('@cnv/ui/pages/support/TicketsPage'));
const TicketDetailPage = lazyWithReload(() => import('@cnv/ui/pages/support/TicketDetailPage'));
const SupportDashboardPage = lazyWithReload(() => import('@cnv/ui/pages/support/SupportDashboardPage'));
const TicketCategoriesPage = lazyWithReload(() => import('@cnv/ui/pages/support/TicketCategoriesPage'));
const TicketSLAPage = lazyWithReload(() => import('@cnv/ui/pages/support/TicketSLAPage'));
const TicketAssignmentRulesPage = lazyWithReload(() => import('@cnv/ui/pages/support/TicketAssignmentRulesPage'));
const TicketCannedResponsesPage = lazyWithReload(() => import('@cnv/ui/pages/support/TicketCannedResponsesPage'));
const TicketSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/support/TicketSettingsPage'));
const WidgetBuilderPage = lazyWithReload(() => import('@cnv/ui/pages/support/WidgetBuilderPage'));

function Placeholder({ title, hint }) {
  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      <p className="text-gray-500 text-sm mt-2">{hint}</p>
    </div>
  );
}

// /suppliers(/:id) → /utilities/suppliers(/:id), giữ query. Đường tắt cũ vẫn mở được trang nhưng khung menu không
// nhận ra trang (sếp Phú báo 01/10/2026 ở Core, Work cùng một bệnh) ⇒ chuyển hẳn về đường chính.
function RedirectSupplierAlias() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/utilities/suppliers${id ? `/${id}` : ''}${search}`} replace />;
}

// Legacy /support/:id → /tickets/:id redirect helper (anh chốt revert 2026-05-17).
function RedirectSupportToTicket() {
  const { id } = useParams();
  return <Navigate to={`/tickets/${id}`} replace />;
}

// /recruitment/campaigns/:id → /recruitment/campaigns/:id/kanban (giữ nguyên query string).
// Link chia sẻ ứng viên (ShareButton) + noti automation trỏ tới /recruitment/campaigns/:id?candidate=<id>,
// trước đây không khớp route nào nên rơi vào catch-all rồi văng về dashboard. KanbanPage đọc ?candidate để mở
// panel chi tiết ứng viên, nên redirect kèm query để bung đúng ứng viên.
function RedirectCampaignToKanban() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/recruitment/campaigns/${id}/kanban${search}`} replace />;
}

// Legacy /crm/reports?r=<key> (Core main.jsx:447 LegacyCrmReportsRedirect) + /crm/reports/<key>
// → /reports/crm/<key>. Trước đây route `/crm/reports/:key` trỏ `<Navigate to="/reports/crm/:key">`:
// react-router KHÔNG thay tham số trong chuỗi đích, nên MỌI link cũ đều nhảy sang URL có chữ
// «:key» nguyên văn → ReportRoute tra catalog không thấy → "Không tìm thấy báo cáo" (bookmark,
// link dán trong chat, link trong noti đều chết). Đọc param bằng useParams mới thay được.
function LegacyCrmReportsRedirect() {
  const { key } = useParams();
  const { search } = useLocation();
  const r = key || new URLSearchParams(search).get('r');
  return <Navigate to={r ? `/reports/crm/${r}` : '/reports/crm'} replace />;
}

// Cụm báo cáo TẮT ở CNV Work SaaS. `module` ở đây là khoá trong REPORTS_CATALOG của cnv-ui —
// KHÔNG phải path.
//
// - `fulfillment` («Triển khai», 14 thẻ): module fulfillment/CDP là CNV-only, KHÔNG port sang
//   SaaS (docs/PORT_GAPS.md, entry `fulfillment_orders` + `fulfillment-accounts`). SaaS không
//   có menu «Triển khai», cũng không mount `/api/v1/fulfillment/*` (chỉ 3 stub trả rỗng ở
//   modules/_stubs) ⇒ cả 14 thẻ đều 404 route_not_found.
// - `work` («Sức khoẻ khách hàng CNV Work», 1 thẻ): báo cáo của Core soi KHÁCH đang dùng CNV
//   Work. Repo này CHÍNH LÀ CNV Work nên không có khái niệm đó; thẻ có Component:null nên bấm
//   vào chỉ ra màn "đang được phát triển".
//
// Vì sao phải chặn ở tầng host: catalog đã gắn `permission: 'fulfillment_reports'` /
// `fulfillment_accounts`, nhưng admin nhận `permissions: ['*']` (api auth/routes/v1.js:137) nên
// qua cửa quyền, vẫn thấy đủ 15 thẻ chết. Thẻ «Gia hạn» nằm ở tab CRM cũng tự ẩn theo, nhờ
// `needs: 'fulfillment'` trong catalog (nó gọi `/fulfillment/reports/cg-renewal`).
// Giữ khớp với cnvconnect-saas/admin/src/App.jsx — cùng lỗi, cùng cách chặn.
const HIDDEN_REPORT_MODULES = ['fulfillment', 'work'];
// Thẻ báo cáo đã có chỗ ở TỐT HƠN nên rút khỏi Kho báo cáo (giữ trong danh mục kernel để
// Core vẫn dùng — chỉ ẩn ở hệ này):
//   · checkin-map → Dự án ▸ Bản đồ check-in (/projects/checkin-map)
// Bookmark cũ /reports/tasks/checkin-map vẫn mở được: ReportRoute không đọc danh sách ẩn của
// Hub cho đường trực tiếp.
const HIDDEN_REPORT_KEYS = ['checkin-map'];

export default function App() {
  return (
    // Global Suspense cover 155 lazy() page (per share-kernel pattern Core).
    // Pre-auth (Login/Signup) eager nên KHÔNG cần Suspense bao quanh — Suspense
    // chỉ kick in khi render lazy component.
    <>
    <Suspense fallback={<div className="flex justify-center items-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      {/* Quên mật khẩu — công khai, KHÔNG RequireAuth (người dùng đang không đăng nhập được). */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/setting-up" element={<SettingUpPage />} />
      {/* Public invite accept page — KHÔNG cần RequireAuth. NV click link từ email. */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      {/* Public share link (ADR-032) — KHÁCH HÀNG bấm link "Xem / In" nhận qua email/zalo.
          KHÔNG RequireAuth: trước đây rơi vào RequireAuth → khách thấy màn hình ĐĂNG NHẬP.
          Path giữ 1:1 Core (/q/:id, /inv/:id); `:id` ở SaaS là share token 32 hex thay vì
          prefix uuid của Core (chống dò + resolve tenant) — xem ADR-032. */}
      <Route path="/q/:id" element={<PublicQuotationPage />} />
      <Route path="/inv/:id" element={<PublicInvoicePage />} />
      {/* Bản in NỘI BỘ (01/10/2026, như Core 06/09/2026) — nút "Xem / In" của nhân viên mở đường này (cnv-ui cờ
          `share_link_internal_print`). Cùng hai trang in của khách, chỉ khác nguồn nạp: `/crm/.../:id/print-view` đã
          đăng nhập + gác quyền + phạm vi bản ghi, KHÔNG cộng «Khách đã xem N lần», KHÔNG cấp link công khai.
          RequireAuth nhưng KHÔNG AppLayout: trang giấy để in, không khung menu; chưa đăng nhập ⇒ về /login rồi quay lại. */}
      <Route path="/print/q/:id" element={<RequireAuth><PublicQuotationPage internal /></RequireAuth>} />
      <Route path="/print/inv/:id" element={<RequireAuth><PublicInvoicePage internal /></RequireAuth>} />
      {/* Lịch xếp việc chia sẻ — link `/s/schedule/<token>` do máy chủ dựng (`url_path`), người nhận
          không có tài khoản ⇒ NGOÀI RequireAuth. Token lạ / thu hồi / hết hạn: trang tự báo. */}
      <Route path="/s/schedule/:token" element={<PublicSchedulePage apiBase={PUBLIC_API_BASE} />} />
      {/* Phiếu nghỉ việc điền sau khi nghỉ — người nghỉ đã bị khoá tài khoản ⇒ NGOÀI RequireAuth. */}
      {/* + biểu mẫu lead host sẵn (khoá `lf_…`) — xem PublicFormRoute. */}
      <Route path="/f/:token" element={<PublicFormRoute />} />
      {/* Workspace selector — yêu cầu login nhưng KHÔNG mount AppLayout (chưa có active tenant). */}
      <Route path="/select-workspace" element={<WorkspaceSelectorPage />} />
      {/* Màn đồng ý OAuth của đầu nối MCP (claude.ai / app Claude / ChatGPT «Kết nối CNV Work» — docs/MCP_OAUTH.md).
          NGOÀI RequireAuth + AppLayout: trang tự đưa sang /login và cất đường quay lại (RequireAuth không làm được việc
          đó — LoginPage dùng chung không đọc state.from). */}
      <Route path="/oauth/authorize" element={<OAuthConsentPage />} />
      <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
        {/* TC-292: mặc định về /dashboard (luôn hợp lệ). Trước đây ép /chat — tenant tắt module
            team_chat sẽ vào /chat lỗi "module chưa enable". /dashboard an toàn cho mọi tenant. */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        {/* Admin manage invitations (mời nhân viên) */}
        <Route path="/users/invitations" element={<InvitationsPage />} />

        {/* Inbox group — match Core adminMenu chat group.
            Conversation selection dùng ?id=<uuid> query param (theo Core), không phải path /inbox/:id. */}
        <Route path="/inbox" element={<InboxPage />} />
        {/* Chat (Slack/Lark) — gated bằng tenant.private_modules.team_chat=true. */}
        <Route path="/chat" element={<TeamChatPage />} />
        <Route path="/chat/:roomId" element={<TeamChatPage />} />
        <Route path="/inbox/canned-responses" element={<ChatCannedResponsesPage />} />
        <Route path="/inbox/agents" element={<ChatAgentsPage />} />
        <Route path="/inbox/agents/:id" element={<ChatAgentDetailPage />} />
        <Route path="/inbox/knowledge-base" element={<KnowledgeBasePage />} />
        {/* 1 trang 2 tab → 2 mục menu: only='escalations' (Trợ giúp AI) / 'learned' (Huấn luyện) */}
        <Route path="/inbox/ai-assist" element={<ChatAITrainingPage only="escalations" />} />
        <Route path="/inbox/ai-training" element={<ChatAITrainingPage only="learned" />} />
        <Route path="/inbox/ai-logs" element={<ChatAILogsPage />} />
        <Route path="/inbox/channels" element={<ChannelsPage />} />
        {/* SaaS-only: Core không có. Web chỉ SOẠN, đợt gửi chạy ở ứng dụng máy tính. */}
        <Route path="/inbox/zalo-campaigns" element={<ZaloCampaignsPage />} />
        <Route path="/inbox/staff" element={<ChatStaffPage />} />
        <Route path="/inbox/config" element={<ChatConfigPage />} />

        {/* Inbox Reports (Phase D2 — Core's main.jsx có /inbox/{metrics,qc-reviews,roi-report,performance-report,reports}) */}
        <Route path="/inbox/metrics" element={<ChatMetricsPage />} />
        <Route path="/inbox/qc-reviews" element={<ChatQcReviewsPage />} />
        <Route path="/inbox/roi-report" element={<ChatRoiReportPage />} />
        <Route path="/inbox/performance-report" element={<ChatPerformanceReportPage />} />
        <Route path="/inbox/customer-engagement" element={<ChatCustomerEngagementReportPage />} />
        <Route path="/inbox/reports" element={<ChatReportsPage />} />

        {/* CRM group (M4 — Sales scope ADR-022 internal-only).
            Routes match Core adminMenu /crm/* paths.
            Path `/crm` redirect → /crm dashboard. */}
        <Route path="/crm" element={<CrmDashboardPage />} />
        <Route path="/crm/leads" element={<CrmLeadsPage />} />
        <Route path="/crm/leads/:id" element={<CrmLeadDetailPage />} />
        <Route path="/crm/contacts" element={<CrmContactsPage />} />
        <Route path="/crm/contacts/:id" element={<CrmContactDetailPage />} />
        <Route path="/crm/companies" element={<CrmCompaniesPage />} />
        <Route path="/crm/companies/:id" element={<CrmCompanyDetailPage />} />
        <Route path="/crm/deals" element={<CrmDealsPage />} />
        <Route path="/crm/deals/:id" element={<CrmDealDetailPage />} />
        <Route path="/crm/activities" element={<CrmActivitiesPage />} />
        <Route path="/crm/routines" element={<RoutinesPage />} />
        <Route path="/crm/sources" element={<CrmSourcesPage />} />

        {/* CRM Finance + Promotion (M5 ADR-022 internal-only).
            Routes match Core attendance-app/admin/src/main.jsx /crm/* */}
        <Route path="/crm/quotations" element={<CrmQuotationsPage />} />
        <Route path="/crm/quotations/:id" element={<CrmQuotationDetailPage />} />
        <Route path="/crm/invoices" element={<CrmInvoicesPage />} />
        <Route path="/crm/invoices/:id" element={<CrmInvoiceDetailPage />} />
        <Route path="/crm/targets" element={<CrmTargetsPage />} />
        <Route path="/crm/payments" element={<CrmPaymentsPage />} />
        <Route path="/crm/payment-methods" element={<CrmPaymentMethodsPage />} />
        <Route path="/crm/subscriptions" element={<CrmSubscriptionsPage />} />
        <Route path="/crm/promotions" element={<CrmPromotionsPage />} />
        <Route path="/crm/promotions/:id" element={<CrmPromotionDetailPage />} />

        {/* Core's main.jsx aliases (deep-link redirects):
            - /crm/pipeline → /crm/deals?view=kanban (Pipeline đã merge vào Deals kanban view)
            - /crm/coupons → /crm/promotions (Coupons đã merge vào Promotions page) */}
        <Route path="/crm/pipeline" element={<Navigate to="/crm/deals?view=kanban" replace />} />
        <Route path="/crm/coupons" element={<Navigate to="/crm/promotions" replace />} />

        {/* TODO: 11 pages Core có nhưng SaaS chưa port. Render Placeholder đúng cấu trúc Core
            để anh thấy gap rõ ràng. Khi nào port → thay <Placeholder/> bằng <CrmXPage/>. */}
        {/* Khách hàng 360° (SaaS-only) — aggregate Pháp nhân + Cá nhân. */}
        <Route path="/crm/customers" element={<CrmCustomersPage />} />
        <Route path="/crm/customers/:kind/:id" element={<CrmCustomerDetailPage />} />
        <Route path="/crm/products" element={<CrmProductsPage />} />
        <Route path="/crm/product-categories" element={<CrmProductCategoriesPage />} />
        <Route path="/crm/lead-objectives" element={<CrmLeadObjectivesPage />} />
        <Route path="/crm/credit-notes" element={<CrmCreditNotesPage />} />
        <Route path="/crm/documents" element={<CrmDocumentsPage />} />
        <Route path="/crm/data-transfer" element={<CrmDataTransferPage />} />
        <Route path="/crm/data-transfer-settings" element={<CrmDataTransferSettingsPage />} />
        <Route path="/crm/round-robin" element={<LeadDistributionPage />} />
        <Route path="/crm/commissions" element={<CrmCommissionsPage />} />
        <Route path="/crm/revenue" element={<CrmRevenuePage />} />
        <Route path="/crm/deal-roles" element={<CrmDealRolesPage />} />
        <Route path="/crm/doc-templates" element={<CrmDocTemplatesPage />} />
        <Route path="/crm/quotation-approval" element={<CrmQuotationApprovalSettingsPage />} />
        <Route path="/crm/invoice-billing-settings" element={<CrmInvoiceBillingSettingsPage />} />
        <Route path="/crm/fields" element={<CrmFieldSettingsPage />} />
        {/* Đường cũ (bookmark, link trong tài liệu) ⇒ tab Lead của trang mới. */}
        <Route path="/crm/lead-field-settings" element={<Navigate to="/crm/fields?tab=lead" replace />} />
        <Route path="/crm/industries" element={<CrmIndustriesPage />} />
        <Route path="/crm/duplicate-check" element={<CrmDuplicateSettingsPage />} />
        <Route path="/crm/lead-scoring" element={<CrmLeadScoringPage />} />
        <Route path="/crm/lead-claim-requests" element={<LeadClaimRequestsPage />} />
        {/* Đơn THAM GIA (đồng sở hữu liên hệ/công ty) — CÙNG page shared với đơn bàn giao lead,
            phân biệt bằng prop `variant`/`endpoint` (SLOT pattern, 1 nguồn sự thật).
            Khớp Core main.jsx:842 nguyên xi — đừng đổi prop, đừng clone page thứ hai. */}
        <Route path="/crm/access-requests" element={<LeadClaimRequestsPage endpoint="/crm/access-requests" variant="access" />} />

        {/* Reports Hub — top-level (Core adminMenu line 193-200).
            URL: /reports (all tabs) | /reports/:module (filtered tab) | /reports/:module/:key (specific report).
            Legacy /crm/reports redirect → /reports/crm. */}
        <Route path="/reports" element={<ReportsHub hiddenModules={HIDDEN_REPORT_MODULES} hiddenKeys={HIDDEN_REPORT_KEYS} />} />
        <Route path="/reports/:module" element={<ReportsHub hiddenModules={HIDDEN_REPORT_MODULES} hiddenKeys={HIDDEN_REPORT_KEYS} />} />
        {/* Báo cáo có trang riêng (catalog Component:null) — mount TRƯỚC ReportRoute generic để parity Core (main.jsx). Page đều shared @cnv/ui. */}
        <Route path="/reports/crm/sales-performance" element={<SalesPerformancePage />} />
        <Route path="/reports/crm/forecast" element={<SalesForecastPage />} />
        <Route path="/reports/crm/teams" element={<SalesTeamPage />} />
        <Route path="/reports/hr/attendance" element={<HrAttendanceReportPage />} />
        <Route path="/reports/hr/assets" element={<HrAssetReportPage />} />
        <Route path="/reports/hr/asset-assignments" element={<HrAssetAssignmentReportPage />} />
        <Route path="/reports/crm/commissions" element={<CrmCommissionsPage />} />
        <Route path="/reports/marketing/overview" element={<MarketingReportsPage />} />
        <Route path="/reports/chat/:key" element={<ChatReportsPage />} />
        {/* Thẻ «Thống kê phê duyệt» trong hub có Component:null nên trước đây bấm vào ra màn
            "đang được phát triển" — trong khi trang thật ĐANG CHẠY ở /approvals/report (vào
            được qua secondary nav của Phê duyệt, BE `/approvals/report` đã wire). Đẩy thẳng
            sang đó thay vì mount lại ApprovalReportPage ở đây: /approvals/report nằm trong
            FULLBLEED_PATTERNS của AppLayout, mount ở /reports/* sẽ mất layout full-bleed.
            Core mount page tại /reports/approval/overview (main.jsx:688) — ở SaaS redirect để
            khỏi vỡ full-bleed, đích đến vẫn là cùng một page. */}
        <Route path="/reports/approval/overview" element={<Navigate to="/approvals/report" replace />} />
        <Route path="/reports/:module/:key" element={<ReportRoute hiddenModules={HIDDEN_REPORT_MODULES} />} />
        <Route path="/crm/reports" element={<LegacyCrmReportsRedirect />} />
        <Route path="/crm/reports/:key" element={<LegacyCrmReportsRedirect />} />

        {/* Dashboard (Tổng quan) — M6, match Core adminMenu key='dashboard' */}
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Nhân sự (Employees) — M6.2b shipped: BE + FE port từ Core 1:1 */}
        <Route path="/employees" element={<EmployeesPage />} />
        <Route path="/employees/:id" element={<EmployeeDetailPage />} />
        <Route path="/employees/events" element={<EmployeeEventsPage />} />
        <Route path="/teams" element={<TeamsPage />} />
        <Route path="/org-chart" element={<OrgChartPage />} />
        <Route path="/employees/contracts" element={<ContractsPage />} />
        <Route path="/employees/contract-types" element={<ContractTypesPage />} />
        <Route path="/employees/job-titles" element={<JobTitlesPage />} />
        {/* Lương ẨN 07/10/2026 (sếp Phú: «hệ này làm chưa xong») — xem nhóm «Lương» ở AppLayout.jsx.
        <Route path="/payroll/periods" element={<PayrollPeriodsPage />} />
        <Route path="/payroll/periods/:id" element={<PayrollPeriodDetailPage />} />
        <Route path="/payroll/structures" element={<PayrollStructuresPage />} />
        <Route path="/payroll/levels" element={<PayrollLevelsPage />} />
        <Route path="/payroll/insurance" element={<PayrollInsurancePage />} />
        <Route path="/payroll/tax" element={<PayrollTaxPage />} /> */}
        {/* M6.9 Attendance — full ship (BE + FE 6 pages) */}
        <Route path="/attendance" element={<AttendancePage />} />
        <Route path="/timesheet" element={<WeeklyTimesheetPage />} />
        <Route path="/timesheet/monthly" element={<MonthlyTimesheetPage />} />
        <Route path="/shifts" element={<ShiftsPage />} />
        <Route path="/overtime" element={<OvertimePage />} />
        <Route path="/overtime/policy" element={<OvertimePolicyPage />} />
        <Route path="/roster" element={<RosterPage />} />
        <Route path="/my-roster" element={<MyRosterPage />} />
        <Route path="/shifts/credit-rules" element={<TimesheetCreditRulesPage />} />
        <Route path="/corrections" element={<CorrectionReviewPage />} />
        {/* M6.3 Leaves + Holidays — full ship (BE + FE) */}
        <Route path="/leaves" element={<LeavesPage />} />
        <Route path="/leave-balance" element={<LeaveBalancePage />} />
        <Route path="/holidays" element={<HolidaysPage />} />
        {/* M6.6 Assets — full ship (5 pages) */}
        <Route path="/assets" element={<AssetListPage />} />
        <Route path="/assets/:id" element={<AssetDetailPage />} />
        <Route path="/assets/assignments" element={<AssetAssignmentPage />} />
        <Route path="/assets/audit" element={<AssetAuditPage />} />
        <Route path="/digital-assets" element={<DigitalAssetsPage view="list" />} />
        <Route path="/digital-assets/billing" element={<DigitalAssetsPage view="billing" />} />
        <Route path="/digital-assets/alerts" element={<DigitalAssetsPage view="alerts" />} />
        <Route path="/settings/integrations/microsoft" element={<MicrosoftIntegrationPage />} />
        <Route path="/penalties" element={<LatePenaltyPage />} />
        <Route path="/locations" element={<LocationsPage />} />

        {/* Marketing — M6 wire + Placeholder */}
        {/* M6.8 Marketing — full ship (BE + FE 11 pages, defer OAuth/Webhooks M7) */}
        <Route path="/marketing" element={<MarketingDashboardPage />} />
        <Route path="/marketing/campaigns" element={<MarketingCampaignsPage />} />
        <Route path="/marketing/campaigns/:id" element={<MarketingCampaignDetailPage />} />
        <Route path="/marketing/lead-forms" element={<MarketingLeadFormsPage />} />
        <Route path="/marketing/events" element={<MarketingEventsPage />} />
        <Route path="/marketing/events/:id" element={<MarketingEventDetailPage />} />
        <Route path="/marketing/memberships" element={<MarketingMembershipsPage />} />
        <Route path="/marketing/memberships/:id" element={<MarketingMembershipDetailPage />} />
        <Route path="/marketing/ad-accounts" element={<MarketingAdAccountsPage />} />
        <Route path="/marketing/reports" element={<MarketingReportsPage />} />

        {/* Tiện ích — Nhà cung cấp + Kho in ấn */}
        <Route path="/utilities/exports" element={<ExportsPage />} />
        <Route path="/utilities/suppliers" element={<SupplierListPage />} />
        <Route path="/utilities/suppliers/:id" element={<SupplierDetailPage />} />
        <Route path="/suppliers" element={<RedirectSupplierAlias />} />
        <Route path="/suppliers/:id" element={<RedirectSupplierAlias />} />
        <Route path="/utilities/print-library" element={<PrintLibraryPage />} />
        <Route path="/utilities/company-room" element={<CompanyRoomPage />} />
        <Route path="/utilities/cabin-translate" element={<CabinTranslatePage />} />
        <Route path="/utilities/cabin-translate/:id" element={<CabinSessionPage />} />
        <Route path="/utilities/email-signature" element={<EmailSignaturePage />} />
        <Route path="/whats-new" element={<ProductNewsPage />} />
        <Route path="/whats-new/:id" element={<ProductNewsDetailPage />} />
        <Route path="/utilities/notification-templates" element={<NotificationTemplateListPage />} />
        <Route path="/utilities/notification-templates/:id" element={<NotificationTemplateDetailPage />} />
        <Route path="/utilities/print-library/:id" element={<PrintItemDetailPage />} />
        {/* Backward compat — page navigate dùng /marketing/print-library/:id */}
        <Route path="/marketing/print-library" element={<PrintLibraryPage />} />
        <Route path="/marketing/print-library/:id" element={<PrintItemDetailPage />} />

        {/* Tuyển dụng (Recruitment) — M6 wire + Placeholder */}
        {/* M6.7 Recruitment — full ship (BE + FE 13 pages) */}
        <Route path="/recruitment" element={<RecruitmentDashboard />} />
        <Route path="/recruitment/proposals" element={<ProposalsPage />} />
        <Route path="/recruitment/campaigns" element={<CampaignsPage />} />
        <Route path="/recruitment/campaigns/:id/kanban" element={<KanbanPage />} />
        {/* Trình dựng vòng tuyển RIÊNG của một chiến dịch — mở từ «Cài đặt chiến dịch» → thẻ «Vòng tuyển» (cnv-ui 06/10/2026).
            Trước đó Work không mount đường này ⇒ nút «Pipeline» trên thẻ chiến dịch (cnv-ui điều hướng /recruitment/c/:id/pipeline)
            rơi vào catch-all, văng về tổng quan. Đường dài /recruitment/campaigns/… để gác quyền ăn đúng tiền tố chiến dịch
            (routePermission longest-prefix) — Core mount cả đường này lẫn đường ngắn. */}
        <Route path="/recruitment/campaigns/:id/pipeline" element={<PipelineBuilderPage />} />
        {/* Alias: link chia sẻ ứng viên + noti automation trỏ /campaigns/:id?candidate=<id> → kanban (giữ query) */}
        <Route path="/recruitment/campaigns/:id" element={<RedirectCampaignToKanban />} />
        <Route path="/recruitment/candidates" element={<CandidatesPage />} />
        <Route path="/recruitment/jd-library" element={<JDLibraryPage />} />
        <Route path="/recruitment/onboarding" element={<OnboardingPage />} />
        <Route path="/recruitment/offboarding" element={<OffboardingPage />} />
        <Route path="/recruitment/sources" element={<SourcesPage />} />
        <Route path="/recruitment/settings" element={<RecruitmentSettingsPage />} />
        {/* Đường cũ — giữ chuyển hướng để link đã gửi cho nhau không chết. */}
        <Route path="/recruitment/ai-settings" element={<Navigate to="/recruitment/settings" replace />} />
        <Route path="/recruitment/pipelines" element={<PipelineLibraryPage />} />
        <Route path="/recruitment/pipelines/new" element={<PipelineBuilderPage />} />
        <Route path="/recruitment/pipelines/:id" element={<PipelineBuilderPage />} />
        <Route path="/recruitment/pipeline-settings" element={<PipelineSettingsPage />} />
        {/* Alias /settings/pipelines/* — port 1:1 Core main.jsx:697-699 (Core mount CẢ HAI họ path).
            BẮT BUỘC: page shared cnv-ui hardcode nav sang /settings/pipelines/* ở 6 chỗ
            (PipelineLibraryPage 61/134/152/179, PipelineBuilderPage 379/412, RecruitmentLayout 11,
            CampaignPipelineSelector 68, TicketDetailContent 3872). Thiếu route này → mọi thao tác
            tạo/sửa pipeline rơi vào catch-all "*" → Navigate to="/" → văng về trang tổng quan. */}
        <Route path="/settings/pipelines" element={<PipelineLibraryPage />} />
        <Route path="/settings/pipelines/new" element={<PipelineBuilderPage />} />
        <Route path="/settings/pipelines/:id" element={<PipelineBuilderPage />} />

        {/* Automation — M6 wire single-link Placeholder */}
        {/* M6.11 Automation — full ship (BE + FE 2 pages, engine execute defer M7) */}
        <Route path="/automation" element={<AutomationListPage />} />
        <Route path="/automation/:id" element={<AutomationBuilderPage />} />

        {/* Finance (Tài chính) — M6 wire Placeholder */}
        <Route path="/finance/domains" element={<Placeholder title="Tên miền" hint="TODO M6.10: port domains.js Core 124 LOC" />} />

        {/* Tasks (Công việc) — M6, match Core adminMenu key='tasks' */}
        {/* M6 Tasks — match Core route 1:1: TasksPage là unified với ?view=mine|templates|board|workload */}
        <Route path="/projects" element={<ProjectsOverviewPage />} />
        <Route path="/projects/board" element={<ProjectsBoardPage />} />
        <Route path="/projects/map" element={<ProjectsMapPage />} />
        <Route path="/projects/checkin-map" element={<ProjectsCheckinMapPage />} />
        <Route path="/projects/settings" element={<ProjectsSettingsPage />} />
        <Route path="/tasks/circles" element={<CirclesPage />} />
        {/* Khuôn dùng lại — dời từ cụm Việc sang 03/09/2026. `/tasks?view=templates` và
            `?view=form-templates` chuyển hướng về đây nên link cũ không chết. */}
        <Route path="/projects/templates" element={<ProjectTemplatesPage />} />
        <Route path="/projects/form-templates" element={<ProjectFormTemplatesPage />} />
        {/* Soạn/sửa biểu mẫu là TRANG RIÊNG, không popup (sếp Phú 03/09). `/new` phải đứng
            TRƯỚC `/:id` — không thì "new" bị nuốt làm id. */}
        <Route path="/projects/form-templates/new" element={<ProjectFormTemplateEditPage />} />
        <Route path="/projects/form-templates/:id" element={<ProjectFormTemplateEditPage />} />
        {/* Hồ sơ MỘT dự án. Work chưa từng có màn này — bảng danh sách vẫn trỏ tới nó,
            nên thiếu route là bấm vào tên dự án ra trang trắng. */}
        <Route path="/projects/:id" element={<ProjectProfilePage />} />
        <Route path="/schedule" element={<WorkSchedulePage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/tasks/my-tasks" element={<Navigate to="/tasks?view=mine" replace />} />
        <Route path="/tasks/projects" element={<Navigate to="/tasks?view=mine" replace />} />
        <Route path="/tasks/templates" element={<Navigate to="/tasks?view=templates" replace />} />
        {/* Danh mục biểu mẫu — port từ CNV Connect 23/08/2026. Trang dựng sẵn ở
            `@cnv/ui/pages/tasks/TasksPage` (?view=form-templates) nên hai dòng này chỉ là
            đường tắt cho khớp Connect: biểu mẫu thuộc cụm VIỆC, không phải nhân sự. */}
        <Route path="/tasks/form-template" element={<Navigate to="/tasks?view=form-templates" replace />} />
        <Route path="/tasks/form-templates" element={<Navigate to="/tasks?view=form-templates" replace />} />
        <Route path="/tasks/board" element={<Navigate to="/tasks?view=board" replace />} />
        <Route path="/tasks/workload" element={<Navigate to="/tasks?view=workload" replace />} />
        <Route path="/tasks/stages-settings" element={<TaskStagesSettingsPage />} />
        <Route path="/tasks/stages" element={<Navigate to="/tasks/stages-settings" replace />} />
        <Route path="/tasks/:id" element={<TaskDetailPage />} />

        {/* Approvals (Phê duyệt) — M6, match Core adminMenu key='approvals' */}
        {/* M6 Approvals — match Core 1:1: ApprovalShell wrapper với nested routes (tabs sidebar) */}
        <Route path="/approvals/templates/:id" element={<TemplateBuilderPage />} />
        <Route path="/approvals" element={<ApprovalShell />}>
          <Route index element={<ApprovalInboxPage />} />
          <Route path="inbox" element={<ApprovalInboxPage />} />
          <Route path="templates" element={<ApprovalTemplatesPage />} />
          <Route path="shared" element={<SharedListsPage />} />
          <Route path="report" element={<ApprovalReportPage />} />
        </Route>
        <Route path="/approvals/pending" element={<Navigate to="/approvals?tab=tasks" replace />} />
        <Route path="/approvals/my-requests" element={<Navigate to="/approvals?tab=sent" replace />} />
        <Route path="/approvals/legacy" element={<ApprovalsPage />} />
        <Route path="/approvals/legacy-pending" element={<MyPendingPage />} />
        <Route path="/approvals/legacy-my-requests" element={<MyRequestsPage />} />
        <Route path="/approvals/:id" element={<ApprovalDetailPage />} />

        {/* Hỗ trợ (Ticket / Help desk) — anh chốt 2026-05-17 canonical /tickets/*.
            Bỏ hoàn toàn /support khỏi FE + BE URL — module BE folder name vẫn
            "support" (namespace nội bộ) nhưng URL path = /tickets. */}
        <Route path="/tickets" element={<TicketsPage />} />
        <Route path="/tickets/dashboard" element={<SupportDashboardPage />} />
        <Route path="/tickets/categories" element={<TicketCategoriesPage />} />
        <Route path="/tickets/sla" element={<TicketSLAPage />} />
        <Route path="/tickets/rules" element={<TicketAssignmentRulesPage />} />
        <Route path="/tickets/canned-responses" element={<TicketCannedResponsesPage />} />
        <Route path="/tickets/settings" element={<TicketSettingsPage />} />
        <Route path="/tickets/widget" element={<WidgetBuilderPage />} />
        <Route path="/tickets/:id" element={<TicketDetailPage />} />
        {/* Legacy /support/* → /tickets/* redirect (chuyển canonical 2 lần — bookmark cũ). */}
        <Route path="/support" element={<Navigate to="/tickets" replace />} />
        <Route path="/support/:id" element={<RedirectSupportToTicket />} />

        {/* System group — SaaS-specific Tích hợp + Cài đặt */}
        <Route path="/api-keys" element={<ApiKeysPage />} />
        <Route path="/mcp-keys" element={<McpKeysPage />} />
        <Route path="/webhooks" element={<WebhooksPage />} />
        {/* Đường dẫn CHÍNH thức từ 29.08.2026: trang đã gom cả Billing vào nên "marketplace"
            (cửa hàng ứng dụng) không còn đúng việc nó làm — sếp Phú chỉ ra. */}
        <Route path="/billing" element={<MarketplacePage />} />
        <Route path="/billing/orders/:id" element={<SelfOrderPage />} />
        {/* Đường cũ GIỮ SỐNG, không xoá: link đã gửi cho khách, đã lưu bookmark, đã nằm trong
            task của sales. `/marketplace/orders/:id` render THẲNG (không chuyển hướng) để popup
            thanh toán mở từ Hộp thư không nhấp nháy đổi địa chỉ giữa lúc khách đang trả tiền. */}
        <Route path="/marketplace" element={<Navigate to="/billing" replace />} />
        {/* Đơn tự mua nằm CẠNH kho ứng dụng, không nằm trong /settings: đặt dưới /settings thì
            khung Cài đặt bôi sáng nhầm mục "Cài đặt chung" trong khi người dùng đang xem đơn
            hàng — đo tại chỗ 28.08.2026. */}
        <Route path="/marketplace/orders/:id" element={<SelfOrderPage />} />
        {/* User profile + me settings (avatar click → /me, có logout button) */}
        <Route path="/me" element={<MeSettingsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* <Route path="/profile/payroll" element={<MyPayrollPage />} />  — Lương ẨN 07/10/2026 */}
        {/* M6.12 Settings — 14 pages */}
        <Route path="/settings" element={<SettingsPage />} />
        {/* Gói & thanh toán gom về MỘT chỗ (sếp Phú 29.08.2026): trang Billing cũ trùng gần
            như toàn bộ với Kho ứng dụng — gói hiện tại, số user, dung lượng, lịch sử kích hoạt
            đều đã có ở đó (lịch sử nằm trong từng thẻ khi bung ra). Giữ đường dẫn cũ để link
            đã gửi đi không chết. */}
        <Route path="/settings/billing" element={<Navigate to="/billing" replace />} />
        <Route path="/settings/feature-licenses" element={<FeatureLicensesPage />} />
        <Route path="/settings/audit-log" element={<AuditLogPage />} />
        <Route path="/settings/storage" element={<StorageUsagePage />} />
        <Route path="/settings/email-templates" element={<EmailTemplatesPage />} />
        {/* :id = editor mở 1 mẫu (page điều hướng openEditor → /:id). Port 1:1 Core main.jsx:684.
            Thiếu route này → click mẫu unmount page, editor không mở. */}
        <Route path="/settings/email-templates/:id" element={<EmailTemplatesPage />} />
        {/* Mattermost routes bỏ khỏi SaaS (2026-05-11) */}
        <Route path="/settings/kanban-theme" element={<KanbanThemePage />} />
        <Route path="/settings/notification-logs" element={<NotificationLogsPage />} />
        <Route path="/settings/integrations/chatgpt" element={<IntegrationChatGPTPage />} />
        <Route path="/settings/integrations/claude" element={<IntegrationClaudePage />} />
        {/* Fireflies route bỏ khỏi SaaS (2026-07-11) */}
        {/* CNV Chat route bỏ khỏi SaaS (2026-07-25) */}
        <Route path="/settings/bots" element={<BotsPage />} />
        <Route path="/settings/bots/:id" element={<BotDetailPage />} />
        <Route path="/settings/integrations/cnv-call" element={<EtelecomSettingsPage />} />
        {/* Chi phí AI — menu Hệ thống → trỏ qua ChatAILogsPage hiện đang ở /inbox/ai-logs */}
        <Route path="/settings/ai-usage" element={<ChatAILogsPage />} />
        <Route path="/settings/ai" element={<AiSettingsPage />} />
        <Route path="/settings/google" element={<GoogleSettingsPage />} />
        <Route path="/settings/lead-distribution" element={<LeadDistributionPage />} />
        <Route path="/roles" element={<RolesPage />} />
        <Route path="/permissions" element={<PermissionsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>

    {/* Chat push notifications — browser noti khi có tin /inbox (Zalo/SMS). */}
    <BackgroundWidget name="chat-push"><ChatPushNotifications /></BackgroundWidget>

    {/* Team-chat (/chat) push notifications — global STOMP subscribe, in-tab. */}
    <BackgroundWidget name="team-chat-push"><TeamChatPushNotifications /></BackgroundWidget>

    {/* Web Push — background noti khi tab đóng (service worker + VAPID). */}
    {/* Trong CNV Work desktop sẽ tự skip (Electron ko có push service). */}
    <BackgroundWidget name="web-push"><WebPushRegister /></BackgroundWidget>

    {/* Global command palette — Cmd+K / Ctrl+K mở quick nav (skip /chat). */}
    <BackgroundWidget name="command-palette"><CommandPalette /></BackgroundWidget>

    {/* Phím tắt chụp nhanh — Ctrl/Cmd+Shift+2 → chụp → annotate → tạo ticket / copy ảnh. */}
    <BackgroundWidget name="quick-screenshot"><QuickScreenshotShortcut /></BackgroundWidget>

    {/* Phát hiện web deploy bản mới → banner tải lại + auto-reload khi đổi trang. */}
    <BackgroundWidget name="web-version-watcher"><WebVersionWatcher /></BackgroundWidget>

    {/* Softphone WebRTC (CNV Call) — dialer nổi + overlay cuộc gọi đến + panel thông tin
        khách gọi. Tự ẩn nếu user chưa có máy nhánh (extensionInfo=null). */}
    <BackgroundWidget name="voip"><FloatingCallWidget /></BackgroundWidget>

    {/* Trợ giúp — nhúng widget hỗ trợ của CNV ở Core (Agent CNV Work + mục «Yêu cầu hỗ trợ»), gắn danh
        tính + claim workspace ĐÃ KÝ (GET /help-widget) + màn hình đang mở. Cụm dùng chung Site Connect
        (@cnv/ui support-help); lối vào là nút «Trợ giúp» trên thanh trên cùng. */}
    <BackgroundWidget name="help-widget"><HelpWidgetLoader /></BackgroundWidget>
    </>
  );
}
